import React, { useCallback, useEffect, useState } from "react";
import "../../assets/css/views/learning/courses.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../app/store";
import { useCourses } from "../../hooks/Learning/courses/useCourses";
import { useEvaluation } from "../../hooks/Learning/courses/useEvaluation";
import { CourseTabContent } from "../../components/Learning/Courses/tab/CourseTabContent";
import { LoadingOverlay } from "../../components/Learning/Courses/common/LoadingOverlay";
import { CoursesTabs } from "../../components/Learning/Courses/tab/CoursesTabs";
import { CourseViewerModal } from "../../components/Learning/Courses/modals/CourseViewerModal";
import { EvaluationConfirmModal } from "../../components/Learning/Courses/modals/EvaluationConfirmModal";
import { EvaluationModal } from "../../components/Learning/Courses/modals/EvaluationModal";
import { EvaluationTimer } from "../../components/Learning/Courses/evaluation/EvaluationTimer";
import { QuestionCard } from "../../components/Learning/Courses/evaluation/QuestionCard";
import { ExitEvaluationModal } from "../../components/Learning/Courses/modals/ExitEvaluationModal";
import { CourseApprovedModal } from "../../components/Learning/Courses/modals/CourseApprovedModal";
import { CourseFailedModal } from "../../components/Learning/Courses/modals/CourseFailedModal";
import { TimeBlockedModal } from "../../components/Learning/Courses/modals/TimeBlockedModal";
import { EvaluationCanceledModal } from "../../components/Learning/Courses/modals/EvaluationCanceledModal";
import { Button, TextBox } from "@veneer/core";
import { sanitizeUrl } from "../../utils/sanitizeText";
import { decryptText } from "../../utils/encryptionUtils";

export default function Courses2() {
  const [t] = useTranslation("global");
  const languageId: any = useSelector((state: RootState) => state.languageId);

  /* =======================
     UI STATE (MODALS)
  ======================== */

  const [loading, setLoading] = useState(false);
  const [showModalCourseAvailable, setShowModalCourseAvailable] =
    useState(false);

  const [showModalValidation, setShowModalValidation] = useState(false);
  const [showModalEvaluation, setShowModalEvaluation] = useState(false);
  const [showModalExitValidation, setShowModalExitValidation] = useState(false);
  const [showModalCourseApproved, setShowModalCourseApproved] = useState(false);
  const [showModalFailedCourse, setShowModalFailedCourse] = useState(false);
  const [showModalTimeFailedCourse, setShowModalTimeFailedCourse] =
    useState(false);
  const [showModalEvaluationCanceled, setShowModalEvaluationCanceled] =
    useState(false);
  const [timeRepeat, setTimeRepeat] = useState<number>(NaN);

  /* =======================
     HOOKS
  ======================== */

  const {
    selectedCourseTab,
    setSelectedCourseTab,
    loadingCourses,
    actualCourse,
    openCourseContent,
    availableFiltered,
    inProgressFiltered,
    completedFiltered,
    searchAvailable,
    searchInProgress,
    searchCompleted,
    imagesLoaded,
    resetCoursesData,
    handleChangeSearchAvailable,
    handleChangeSearchInProgress,
    handleChangeSearchCompleted,
    getUrlFromBlob,
  } = useCourses();

  const {
    questions,
    buttonStartEvaluation,
    setButtonStartEvaluation,
    buttonEvaluate,
    score,
    setScore,
    certificate,
    setIsRunning,
    minutes,
    seconds,
    resetEvaluation,
    loadCertificate,
    validateEvaluation,
    evaluateCourse,
    loseEvaluation,
    handleChangeCheckbox,
    handleChangeAnswers,
    handleChangeSelectEvaluation,
  } = useEvaluation();

  /* =======================
   CALLBACKS
======================= */

  const onLoseEvaluation = useCallback(async () => {
    await loseEvaluation(
      actualCourse?.courseID!,
      actualCourse?.isReinforcement!
    );

    resetCoursesData();
    resetEvaluation();
    setShowModalEvaluation(false);
    setShowModalExitValidation(false);
    setShowModalEvaluationCanceled(true);
  }, [
    loseEvaluation,
    actualCourse?.courseID,
    actualCourse?.isReinforcement,
    resetCoursesData,
    resetEvaluation,
  ]);

  /* =======================
   EFFECTS
======================= */

  //Hace que vuelva aprecer el scrol que se desaparecia
  useEffect(() => {
    const anyModalOpen =
      showModalCourseAvailable ||
      showModalValidation ||
      showModalEvaluation ||
      showModalExitValidation ||
      showModalCourseApproved ||
      showModalFailedCourse ||
      showModalTimeFailedCourse ||
      showModalEvaluationCanceled;

    if (!anyModalOpen) {
      document.body.style.overflow = "auto";
    }
  }, [
    showModalCourseAvailable,
    showModalValidation,
    showModalEvaluation,
    showModalExitValidation,
    showModalCourseApproved,
    showModalFailedCourse,
    showModalTimeFailedCourse,
    showModalEvaluationCanceled,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (showModalEvaluation && document.visibilityState === "hidden") {
        onLoseEvaluation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (showModalEvaluation && e.key === "PrintScreen") {
        onLoseEvaluation();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [showModalEvaluation, onLoseEvaluation]);

  /* =======================
     HANDLERS (UI FLOW)
  ======================== */

  const startEvaluationFlow = () => {
    setShowModalValidation(false);
    setShowModalEvaluation(true);
    setIsRunning(true);
  };

  const formatDate = (date: string) => {
    let timestamp = Date.parse(date);
    if (!isNaN(timestamp)) {
      let dateTemp = new Date(date);
      return dateTemp.toLocaleDateString("en-GB");
    }
    return "";
  };

  // Courses.tsx

  const openModalCourse = async (
    courseId: number,
    isReinforcement: boolean
  ) => {
    await openCourseContent(courseId, isReinforcement);

    setShowModalCourseAvailable(true);

    setTimeout(() => {
      setButtonStartEvaluation(false);
    }, 60000);
  };

  const onOpenModalCertificate = async (courseId: number) => {
    setShowModalCourseAvailable(false);
    setLoading(true);

    await loadCertificate(courseId, languageId.value);

    setLoading(false);
    setShowModalCourseApproved(true);
  };

  const onCloseModalCourse = () => {
    setShowModalCourseAvailable(false);
    setButtonStartEvaluation(true);
    resetCoursesData();
    resetEvaluation();
  };

  const handleCloseResult = () => {
    setShowModalCourseApproved(false);
    setShowModalTimeFailedCourse(false);
    setShowModalFailedCourse(false);
    resetCoursesData();
  };

  /* =======================
     TABS CONFIG
  ======================== */

  const emptyMessages: Record<number, string> = {
    0: t("header.noAvailableCourses"),
    1: t("header.noInProgressCourses"),
    2: t("header.noCompletedCourses"),
  };

  const tabs = [
    {
      id: 0,
      label: t("header.availables"),
      content: (
        <>
          <TextBox
            id="searchCoursesAvailable"
            className="searchCourses"
            label={t("header.search")}
            type="search"
            onChange={handleChangeSearchAvailable}
            value={searchAvailable}
          />

          <CourseTabContent
            loading={loadingCourses}
            emptyMessage={emptyMessages[0]}
            courses={availableFiltered}
            onOpenCourse={openModalCourse}
            imagesLoaded={imagesLoaded}
            getImageUrl={getUrlFromBlob}
            formatDate={formatDate}
            t={t}
          />
        </>
      ),
    },
    {
      id: 1,
      label: t("header.inProgress"),
      content: (
        <>
          <TextBox
            id="searchCoursesInProgress"
            className="searchCourses"
            label={t("header.search")}
            type="search"
            onChange={handleChangeSearchInProgress}
            value={searchInProgress}
          />

          <CourseTabContent
            loading={loadingCourses}
            emptyMessage={emptyMessages[1]}
            courses={inProgressFiltered}
            onOpenCourse={openModalCourse}
            imagesLoaded={imagesLoaded}
            getImageUrl={getUrlFromBlob}
            formatDate={formatDate}
            t={t}
          />
        </>
      ),
    },
    {
      id: 2,
      label: t("header.completed"),
      content: (
        <>
          <TextBox
            id="searchCoursesCompleted"
            className="searchCourses"
            label={t("header.search")}
            type="search"
            onChange={handleChangeSearchCompleted}
            value={searchCompleted}
          />

          <CourseTabContent
            loading={loadingCourses}
            emptyMessage={emptyMessages[2]}
            courses={completedFiltered}
            onOpenCourse={openModalCourse}
            imagesLoaded={imagesLoaded}
            getImageUrl={getUrlFromBlob}
            formatDate={formatDate}
            t={t}
          />
        </>
      ),
    },
  ];

  const onValidateStartEvaluation = async (courseId: number) => {
    if (actualCourse?.isReinforcement === undefined) {
      return;
    }

    const result = await validateEvaluation(
      courseId,
      actualCourse.isReinforcement
    );

    if (result.blocked) {
      setTimeRepeat(result.timeRepeat);
      setShowModalTimeFailedCourse(true);
      return;
    }
    setShowModalCourseAvailable(false);
    setButtonStartEvaluation(true);

    setTimeout(() => {
      setShowModalValidation(true);
    }, 300);
  };

  const onEvaluate = async () => {
    let result: {
      approved: any;
      score: any;
    };

    if (!actualCourse) return;

    result = await evaluateCourse(
      questions,
      actualCourse.courseID,
      actualCourse.isReinforcement
    );

    setScore(result.score);

    if (result.approved === 0) {
      setShowModalFailedCourse(true);
    } else if (result.approved === 1 && actualCourse) {
      setShowModalEvaluation(false);
      setLoading(true);
      await loadCertificate(actualCourse.courseID, languageId.value);
      setLoading(false);
      setShowModalCourseApproved(true);
    }

    setIsRunning(false);
    setShowModalEvaluation(false);
  };

  /* =======================
     RENDER
  ======================== */

  return (
    <>
      <div className="coursesWrapper">
        {loading && <LoadingOverlay />}

        <div className="coursesDiv">
          <CoursesTabs
            selectedTabId={selectedCourseTab}
            onChangeTab={setSelectedCourseTab}
            tabs={tabs}
          />
        </div>

        {/* COURSE VIEWER */}
        <CourseViewerModal
          show={showModalCourseAvailable}
          onClose={onCloseModalCourse}
          selectedCourseTab={selectedCourseTab}
          actualCourse={actualCourse}
          buttonStartEvaluation={buttonStartEvaluation}
          onOpenModalCertificate={onOpenModalCertificate}
          onValidateStartEvaluation={onValidateStartEvaluation}
          sanitizeUrl={sanitizeUrl}
          decryptText={decryptText}
          t={t}
        />

        {/* CONFIRM EVALUATION */}
        <EvaluationConfirmModal
          show={showModalValidation}
          onClose={() => setShowModalValidation(false)}
          onConfirm={startEvaluationFlow}
          actualCourse={actualCourse}
          t={t}
        />

        {/* EVALUATION */}
        <EvaluationModal
          show={showModalEvaluation}
          onClose={() => setShowModalExitValidation(true)}
          t={t}
        >
          <EvaluationTimer minutes={minutes} seconds={seconds} />

          {questions.map((question, index) => (
            <QuestionCard
              key={question.evaluationQuestionID}
              question={question}
              index={index}
              handleChangeCheckbox={handleChangeCheckbox}
              handleChangeAnswers={handleChangeAnswers}
              handleChangeSelectEvaluation={handleChangeSelectEvaluation}
            />
          ))}

          <Button disabled={buttonEvaluate} onClick={onEvaluate}>
            {t("header.evaluate")}
          </Button>
        </EvaluationModal>

        {/* EXIT EVALUATION */}
        <ExitEvaluationModal
          show={showModalExitValidation}
          onClose={() => setShowModalExitValidation(false)}
          onConfirm={onLoseEvaluation}
          t={t}
        />

        {/* RESULT MODALS */}
        <CourseApprovedModal
          show={showModalCourseApproved}
          onClose={handleCloseResult}
          certificate={certificate}
          sanitizeUrl={sanitizeUrl}
          decryptText={decryptText}
          t={t}
        />

        <CourseFailedModal
          show={showModalFailedCourse}
          onClose={handleCloseResult}
          score={Number(score)}
          t={t}
        />

        <TimeBlockedModal
          show={showModalTimeFailedCourse}
          timeRepeat={timeRepeat}
          onClose={() => setShowModalTimeFailedCourse(false)}
          t={t}
          actualCourse={String(actualCourse?.courseName)}
        />

        <EvaluationCanceledModal
          show={showModalEvaluationCanceled}
          onClose={() => setShowModalEvaluationCanceled(false)}
          t={t}
        />
      </div>
    </>
  );
}
